
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import GenericTable from "@/components/ui/GenericTable";

import {
    getOffers,
    getTrailOffers,
    updateOffer,
    updateTrailOffer,
} from "@/services/offers";

import { useScreenPermission } from "@/hooks/useScreenPermission";

import Pagination from "../common/Pagination";
import TrailOfferModal from "./AddTrailOffer";

export default function TrailOfferList() {
    const [loading, setLoading] = useState(false);
    const [offers, setOffers] = useState<any[]>([]);
    const [editingOffer, setEditingOffer] = useState<any | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const { canCreate, canUpdate } = useScreenPermission("trails offer");

    const fetchOffers = async () => {
        try {
            setLoading(true);

            const response = await getTrailOffers();

            const responseData =
                response.data as any;

            if (response.status === 200) {
                setOffers(
                    responseData || []
                );
            }
        } catch (error) {
            toast.error(
                "Failed to fetch offers"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOffers();
    }, [currentPage]);

    const handleToggleStatus =
        async (item: any) => {
            try {
                const formData = new FormData();

                formData.append(
                    "Status",
                    String(!item.Status)
                );

                const response = await updateTrailOffer(
                    item.id,
                    formData
                );

                if (response.status === 200) {
                    toast.success(
                        "Status updated successfully"
                    );

                    setOffers((prev) =>
                        prev.map((x) =>
                            x.id === item.id
                                ? {
                                    ...x,
                                    Status:
                                        !x.Status,
                                }
                                : x
                        )
                    );
                }
            } catch (error) {
                toast.error(
                    "Failed to update status"
                );
            }
        };

    const handleAdd = () => {
        setEditingOffer(null);
        setIsModalOpen(true);
    };

    const handleEdit = (item: any) => {
        setEditingOffer(item);
        setIsModalOpen(true);
    };

    const columns = [
        {
            key: "Image",
            label: "Image",
            render: (value: string) => {
                const imageBaseUrl =
                    import.meta.env.VITE_BASE_URL.replace(
                        "/api",
                        ""
                    );

                const imageUrl = value
                    ? `${imageBaseUrl}${value}`
                    : null;

                return imageUrl ? (
                    <img
                        src={imageUrl}
                        alt="Offer"
                        className="w-16 h-16 rounded-xl object-cover border"
                        onError={(e) => {
                            e.currentTarget.src =
                                "https://via.placeholder.com/100?text=No+Image";
                        }}
                    />
                ) : (
                    <div className="w-16 h-16 rounded-xl border flex items-center justify-center text-xs text-gray-500">
                        No Image
                    </div>
                );
            },
        },

        {
            key: "Days",
            label: "Days",
            render: (value: string) => (
                <p className="font-semibold text-gray-800">
                    {value}
                </p>
            ),
        },
        {
            key: "Status",
            label: "Status",
            render: (_: any, row: any) => (
                <button
                    onClick={() => {
                        if (canUpdate) {
                            handleToggleStatus(row);
                        }
                    }}
                    disabled={!canUpdate}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${row.Status
                            ? "bg-green-500"
                            : "bg-red-500"
                        } ${!canUpdate
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer"
                        }`}
                >
                    <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${row.Status
                                ? "translate-x-6"
                                : "translate-x-1"
                            }`}
                    />
                </button>
            ),
        }
    ];

    return (
        <>
            <div className="space-y-6">
                <Pagination
                    currentPage={
                        currentPage
                    }
                    totalPages={
                        totalPages
                    }
                    totalCount={
                        totalCount
                    }
                    itemsPerPage={20}
                    onPageChange={(
                        page
                    ) =>
                        setCurrentPage(
                            page
                        )
                    }
                />
                <GenericTable
                    title="Trail Offer"
                    buttonName={
                        canCreate
                            ? "Add Trail Offer"
                            : ""
                    }
                    handleAdd={
                        canCreate
                            ? handleAdd
                            : undefined
                    }
                    columns={columns}
                    data={offers}
                    loading={loading}
                    onEdit={
                        canUpdate
                            ? handleEdit
                            : undefined
                    }
                    emptyMessage="No trail offers found"
                />

            </div>

            {isModalOpen && (
                <TrailOfferModal
                    key={
                        editingOffer?.id ||
                        "new"
                    }
                    offer={editingOffer}
                    onClose={() => {
                        setIsModalOpen(
                            false
                        );

                        setEditingOffer(
                            null
                        );
                    }}
                    onSuccess={() => {
                        setIsModalOpen(
                            false
                        );

                        fetchOffers();
                    }}
                />
            )}
        </>
    );
}

